import '../../../Templates/Templates.scss'
import { type FC, type ReactNode, useState } from 'react'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { formatDate } from '../../../../../common/dateUtils'
import { type TemplateValue } from '../../../../../models/TemplateLibrary'
import { type User } from '../../../../../models/User'
import { StoredEmailPreview } from '../../../Templates/EmailTemplateEditor/StoredEmailPreview'
import { matchesSearch } from '../../../Templates/SchedulingEmails/schedulingEmails'
import { SystemUserBadge, TemplateCreatedByUserBadge } from '../../../Templates/TemplateCreatedByUserBadge'
import { TemplateScopeBadge } from '../../../Templates/TemplateScopeBadge'

export const NeedsFixingBadge: FC = () => (
  <span className='paragraph-extra-small rounded-2 px-2 zocks-py-0.125 text-nowrap zocks-bg-color-danger-background zocks-text-danger-200'>Needs fixing</span>
)

interface TemplateOptionProps {
  template: TemplateValue
  isCurrent: boolean
  currentLabel: string
  isSelected: boolean
  isDisabled: boolean
  createdByUser?: User
  onClick: () => void
}

const TemplateOption: FC<TemplateOptionProps> = ({ template, isCurrent, currentLabel, isSelected, isDisabled, createdByUser, onClick }) => (
  <button
    type='button'
    className={classNames('template-item d-flex flex-column gap-2 p-3 w-100 text-start zocks-bg-color-white', {
      'template-item-selected': isSelected,
      'opacity-50': isDisabled,
    })}
    aria-pressed={isSelected}
    disabled={isDisabled}
    onClick={onClick}
  >
    <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 w-100 paragraph-small'>
      <span className='d-flex flex-wrap align-items-center gap-2 text-nowrap'>
        <TemplateScopeBadge scope={template.scope} />
        {isDisabled ? <NeedsFixingBadge /> : null}
      </span>
      {isCurrent ? <span className='paragraph-small fw-bold zocks-text-primary text-nowrap'>{currentLabel}</span> : null}
    </div>
    <span className='paragraph-medium fw-bold zocks-text-gray-900'>{template.name}</span>
    <span className='paragraph-small zocks-text-gray-700'>{template.description}</span>
    <div className='d-flex flex-column zocks-gap-0.25 paragraph-extra-small'>
      {createdByUser ? <TemplateCreatedByUserBadge createdByUser={createdByUser} /> : template.scope === 'system' ? <SystemUserBadge /> : null}
      {'updatedAt' in template ? (
        <span className='d-flex align-items-center gap-1'>
          <i className='bi bi-calendar' aria-hidden='true' />
          Updated {formatDate(new Date(template.updatedAt))}
        </span>
      ) : null}
    </div>
  </button>
)

export interface TemplatePreviewPanelProps {
  template: TemplateValue
  values?: Record<string, string>
  actions?: ReactNode
}

export const TemplatePreviewPanel: FC<TemplatePreviewPanelProps> = ({ template, values, actions }) => (
  <div className='d-flex flex-column gap-3 p-3 border rounded-3 zocks-border-color-gray-200 h-100'>
    <div className='d-flex flex-wrap justify-content-between align-items-start gap-3 pb-3 border-bottom zocks-border-color-gray-200'>
      <div className='template-preview-title d-flex flex-column'>
        <span className='paragraph-large fw-bold zocks-text-gray-900'>{template.name}</span>
        <span className='paragraph-small zocks-text-gray-600'>{template.description}</span>
      </div>
      {actions}
    </div>
    <StoredEmailPreview template={template.template} values={values} />
  </div>
)

export interface TemplatePickerProps {
  templates: TemplateValue[]
  selected: TemplateValue
  currentTemplateId?: string
  currentLabel?: string
  usersById: Record<string, User | undefined>
  values?: Record<string, string>
  actions?: ReactNode
  isDisabled?: (template: TemplateValue) => boolean
  onSelect: (templateId: string) => void
}

export const TemplatePicker: FC<TemplatePickerProps> = ({
  templates,
  selected,
  currentTemplateId,
  currentLabel = 'Current',
  usersById,
  values,
  actions,
  isDisabled = () => false,
  onSelect,
}) => {
  const [search, setSearch] = useState('')
  const visibleTemplates = templates.filter((template) => matchesSearch(template, search))

  return (
    <div className='row g-3'>
      <div className='col-4 d-flex flex-column gap-2'>
        <div className='d-flex align-items-center gap-2 border rounded zocks-border-color-gray-500 px-2'>
          <i className='bi bi-search' aria-hidden='true' />
          <Form.Control
            type='text'
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder='Search templates'
            aria-label='Search templates'
            className='border-0 px-0'
          />
        </div>
        {visibleTemplates.length === 0 ? (
          <p className='paragraph-small zocks-text-gray-600 text-center py-3 mb-0'>No templates match &ldquo;{search}&rdquo;.</p>
        ) : (
          visibleTemplates.map((template) => (
            <TemplateOption
              key={template.id}
              template={template}
              isCurrent={template.id === currentTemplateId}
              currentLabel={currentLabel}
              isSelected={template.id === selected.id}
              isDisabled={isDisabled(template)}
              createdByUser={'createdBy' in template ? usersById[template.createdBy] : undefined}
              onClick={() => onSelect(template.id)}
            />
          ))
        )}
      </div>
      <div className='col-8'>
        <TemplatePreviewPanel template={selected} values={values} actions={actions} />
      </div>
    </div>
  )
}
